import React from "react";
import { Box, Typography } from "@mui/material";
import color from "../../Constants/colors";
import { useDashboardColors } from "./dashboardColors";

interface DashboardCardProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}

export const cardSx = {
  backgroundColor: color.white,
  border: `1px solid ${color.gray200}`,
  borderRadius: "16px",
};

/** Section title with the accent bar every dashboard card starts with. */
export const CardTitle: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => {
  const { primary, accent } = useDashboardColors();

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          aria-hidden
          sx={{
            width: 4,
            height: 16,
            flexShrink: 0,
            borderRadius: "2px",
            backgroundColor: accent,
          }}
        />
        <Typography
          component="h2"
          sx={{
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "22px",
            color: primary,
          }}
        >
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: "18px",
            color: color.gray500,
            mt: 0.25,
            pl: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

/** The white card every home dashboard section sits in. */
const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  subtitle,
  action,
  children,
}) => (
  <Box component="section" sx={{ ...cardSx, p: 2.5 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        mb: 2,
      }}
    >
      <CardTitle title={title} subtitle={subtitle} />
      {action && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            minHeight: 22,
          }}
        >
          {action}
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

export const CardLinkButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => {
  const { accentText } = useDashboardColors();

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        border: 0,
        background: "none",
        cursor: "pointer",
        px: 0.5,
        py: 0,
        mr: -0.5,
        borderRadius: "6px",
        fontSize: 13,
        lineHeight: "22px",
        fontWeight: 600,
        color: accentText,
        whiteSpace: "nowrap",
        "&:hover": { textDecoration: "underline" },
        "&:focus-visible": {
          outline: `2px solid ${accentText}`,
          outlineOffset: 1,
        },
      }}
    >
      {children}
    </Box>
  );
};

export const EmptyState: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      py: 3,
      px: 2,
      borderRadius: "12px",
      border: `1px dashed ${color.gray300}`,
      backgroundColor: color.gray50,
      textAlign: "center",
      fontSize: 13,
      color: color.gray500,
    }}
  >
    {children}
  </Box>
);

export default DashboardCard;
